import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { AxiosError } from 'axios'

import { Dialog, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'

import { UserPoll, UpdateUserPollResponse, UpdateUserPollData } from '../../types'
import { updateUserPoll } from '../../api'
import { constants } from '../../constansts'

const rules = {
  question: [
    {
      validate: (value: string) => value.length <= 254,
      message: 'No puede superar los 254 caracteres',
    },
  ],
}

const requiredFields = ['question', 'expiryDate', 'active', 'defaultDashboard']

interface UpdateUserPollDialogProps {
  handleClose: () => void
  userPollToUpdate: UserPoll
  onDone: (newUserPoll: UserPoll) => void
}

const UpdateUserPollDialog: React.FC<UpdateUserPollDialogProps> = ({
  handleClose,
  onDone,
  userPollToUpdate,
}) => {
  const [question, setQuestion] = useState(userPollToUpdate.question ?? '')
  const [active, setActive] = useState(userPollToUpdate.active)
  const [detail] = useState(userPollToUpdate.detail)
  const [defaultDashboard, setDefaultDashboard] = useState(userPollToUpdate.defaultDashboard)

  const [updateUserPollApi, isLoading] = useApiCall<UpdateUserPollData, UpdateUserPollResponse>(
    updateUserPoll,
  )

  const handlequestionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setQuestion(e.target.value)
  }

  const handleChangeActive: React.ChangeEventHandler<HTMLInputElement> = () => {
    setActive(!active)
  }

  const handleChangeDefaultDashboard: React.ChangeEventHandler<HTMLInputElement> = () => {
    setDefaultDashboard(!defaultDashboard)
  }

  const fields = { question, active }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(fields, rules, requiredFields)
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading

  const onUpdateUserPoll = async () => {
    const userPoll = {
      question,
      active,
      defaultDashboard,
    }
    const payload = {
      id: userPollToUpdate.id,
      payload: userPoll,
    }
    try {
      const { userPoll: updatedUserPoll } = await updateUserPollApi(payload)
      onDone(updatedUserPoll)
    } catch (err) {
      if ((err as AxiosError).message === 'DEFAULT_DASHBOARD_ERROR') {
        snackbar.show('Ya existe una pregunta por defecto para el dashboard')
        return
      }
      console.error(err)
      snackbar.show(`No se pudo editar ${constants.TEST_NAME}. Intente de nuevo.`)
    }
  }

  return (
    <Dialog
      title={`Editar ${constants.TEST_NAME}`}
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateUserPoll}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CustomTextField
            label="Pregunta"
            value={question}
            fullWidth
            onChange={handlequestionChange}
            disabled={isLoading}
            multiline
            maxLength={254}
            error={errors.question.hasError}
            helperText={errors.question.message}
          />
        </Grid>
        <Grid item xs={11}>
          {detail.length > 1 && (
            <CustomFormControlLabel
              control={
                <CustomCheckbox onChange={handleChangeActive} checked={active !== null && active} />
              }
              label="Activar"
            />
          )}
        </Grid>
        <Grid item xs={11}>
          <CustomFormControlLabel
            control={
              <CustomCheckbox onChange={handleChangeDefaultDashboard} checked={defaultDashboard} />
            }
            label="Default Dashboard"
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UpdateUserPollDialog }
